/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useReducer, useEffect, useMemo } from "react";
import { When } from "react-if";
import { Button, theme } from "@nordcloud/gnui";
import { AuditLogIdentityType } from "~/generated/graphql";
import { Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDropdown } from "~/components/SidebarWithMultiSelect/SelectableDropdown";
import { SelectableListWithInput } from "~/components/SidebarWithMultiSelect/SelectableListWithInputs";
import { getNestedSelectionsText } from "~/components/SidebarWithMultiSelect/utils";
import { useDisclosure, useQueryState } from "~/hooks";
import {
  isNotEmpty,
  isNotNil,
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
} from "~/tools";
import { normalizeDateRange } from "~/utils/dates";
import { useGetAuditLogs } from "../hooks/useGetAuditLogs/useGetAuditLogs";
import { getDefaultTimeRange } from "../utils";
import {
  operationCheckboxes,
  targetCheckboxes,
  identityCheckboxes,
} from "./constants";
import { AuditLogsQuery, State } from "./types";

type AuditLogsQueryWithPagination = AuditLogsQuery & {
  page: number;
  limit: number;
};

function buildState(queryState: AuditLogsQuery) {
  const normalizedDateRange = normalizeDateRange(
    queryState.start,
    queryState.end
  );

  return {
    ...queryState,
    operation: queryState.operation,
    target: queryState.target,
    identityType: queryState.identityType,
    identity: queryState.identity,
    start: normalizedDateRange.start,
    end: normalizedDateRange.end,
  };
}

function buildQueryStateFromSidebarState(
  queryState: AuditLogsQueryWithPagination,
  state: State
): AuditLogsQueryWithPagination {
  return {
    ...queryState,
    operation: state.operation,
    target: state.target,
    identityType: state.identityType,
    identity: state.identity,
  };
}

type Props = {
  resultsCount: number;
};

// eslint-disable-next-line complexity
export function AuditLogsFiltersSidebar({ resultsCount }: Props) {
  const { isOpen, open, close } = useDisclosure();

  const { queryState, updateQueryState } = useQueryState<AuditLogsQuery>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );

  const appliedState = buildState(queryState);

  const hasPendingChanges = hasFilterChanges(state, appliedState, {});

  const identityTypeAndIdentitySelectionText = getNestedSelectionsText({
    primaryCount: state.identityType ? 1 : 0,
    primaryLabel: "Identity Type",
    secondaryCount: state.identity ? 1 : 0,
    secondaryLabel: "Identity",
    modeLabel: "Include",
  });

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);

  const defaultTimeRange = getDefaultTimeRange();

  const { count: logsPreviewCount, loading: logsPreviewLoading } =
    useGetAuditLogs({
      page: draftQueryState.page ?? 1,
      limit: draftQueryState.limit ?? 20,
      timeRangeStart: draftQueryState.start ?? defaultTimeRange.start,
      timeRangeEnd: draftQueryState.end ?? defaultTimeRange.end,
      filters: {
        operation: state.operation ?? undefined,
        target: state.target ?? undefined,
        identityType: (state.identityType as AuditLogIdentityType) ?? undefined,
        identity: state.identity || undefined,
      },
      skip: !isOpen || !hasPendingChanges,
    });

  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading: logsPreviewLoading,
    previewResultsCount: logsPreviewCount,
    appliedResultsCount: resultsCount,
    entityLabel: "audit logs",
    defaultLabelWhenNoAppliedCount: true,
  });

  const baseFilterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "audit logs",
    excludeFilterMap: {},
  });

  const helperText =
    previewFooterState.helperTextOverride ?? baseFilterSidebarState.helperText;

  const filterSidebarState = {
    ...baseFilterSidebarState,
    helperText,
  };

  const clearOperationFilter = () => {
    updateState({ operation: undefined });
  };

  const clearTargetFilter = () => {
    updateState({ target: undefined });
  };

  const clearIdentityTypeAndIdentityFilter = () => {
    updateState({
      identityType: undefined,
      identity: undefined,
    });
  };

  const applyFilters = () => {
    updateQueryState({
      ...queryState,
      operation: state.operation,
      target: state.target,
      identityType: state.identityType,
      identity: state.identity,
      page: "1",
    });

    close();
  };

  const clearAllFilters = () => {
    updateState({
      operation: undefined,
      target: undefined,
      identityType: undefined,
      identity: undefined,
    });

    updateQueryState({
      operation: undefined,
      target: undefined,
      identityType: undefined,
      identity: undefined,
    });

    close();
  };

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={filterSidebarState.helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <FilterWrapper
            name="Target"
            isSelected={isNotNil(state.target) && isNotEmpty(state.target)}
            selectedOptions={state.target ? [state.target] : []}
            onClear={clearTargetFilter}
          >
            <SelectableDropdown
              options={targetCheckboxes}
              selectedOption={state.target ?? undefined}
              onSelect={(selected: string) => {
                updateState({
                  ...state,
                  target: selected,
                });
              }}
              onClear={() => {
                updateState({
                  ...state,
                  target: undefined,
                });
              }}
            />
          </FilterWrapper>
          <FilterWrapper
            name="Operation"
            isSelected={
              isNotNil(state.operation) && isNotEmpty(state.operation)
            }
            selectedOptions={state.operation ? [state.operation] : []}
            onClear={clearOperationFilter}
          >
            <SelectableDropdown
              options={operationCheckboxes}
              selectedOption={state.operation ?? undefined}
              onSelect={(selected: string) => {
                updateState({
                  ...state,
                  operation: selected,
                });
              }}
              onClear={() => {
                updateState({
                  ...state,
                  operation: undefined,
                });
              }}
            />
          </FilterWrapper>
          <FilterWrapper
            name="Identity"
            isSelected={
              (isNotNil(state.identityType) &&
                isNotEmpty(state.identityType)) ||
              (isNotNil(state.identity) && isNotEmpty(state.identity))
            }
            selectedOptions={state.identityType ? [state.identityType] : []}
            selectedText={identityTypeAndIdentitySelectionText}
            onClear={clearIdentityTypeAndIdentityFilter}
          >
            <SelectableListWithInput
              showSearchBar={false}
              isMultiSelectAllowed={false}
              selectableItems={identityCheckboxes}
              selectedOptions={{
                checkboxes: state.identityType ? [state.identityType] : [],
                inputsByCheckbox: state.identityType
                  ? {
                      [state.identityType]: [state.identity ?? ""],
                    }
                  : {},
              }}
              onMultiSelect={(selections) => {
                const identityType = selections.checkboxes[0];
                updateState({
                  ...state,
                  identityType: identityType,
                  identity: selections.inputsByCheckbox?.[identityType]?.[0],
                });
              }}
            />
          </FilterWrapper>
        </Sidebar>
      </When>
    </>
  );
}
